import { useParams, Link, Navigate } from "react-router-dom";
import { CheckCircle2, ShieldCheck, ArrowRight, Lock } from "lucide-react";
import { Breadcrumbs, Reveal, SectionHeader, PrimaryButton, SecondaryButton } from "../components/common";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { SERVICES, FAQS } from "../data/site";

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = SERVICES[slug];
  if (!s) return <Navigate to="/services" replace />;

  const related = Object.entries(SERVICES).filter(([k]) => k !== slug).slice(0, 3);

  const blocks = [
    { title: "What this service includes", items: s.includes },
    { title: "Who it is for", items: s.forWho },
    { title: "Typical use cases", items: s.useCases },
    { title: "Client inputs required", items: s.inputs },
  ];

  return (
    <>
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Services", to: "/services" }, { label: s.title }]} />
      <section className="container-page py-12 lg:py-16">
        <span className="eyebrow">{s.eyebrow}</span>
        <h1 className="mt-4 max-w-3xl font-heading text-[34px] font-800 leading-[1.1] text-brand-ink md:text-[50px]">{s.title}</h1>
        <p className="mt-4 max-w-2xl text-[19px] leading-relaxed text-brand-muted md:text-[21px]">{s.lead}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <PrimaryButton to="/contact" testid="service-cta">Discuss This Service</PrimaryButton>
          <SecondaryButton to="/contact?type=sample" testid="service-sample">Request a Sample</SecondaryButton>
        </div>
      </section>

      <section className="pb-16">
        <div className="container-page grid gap-6 md:grid-cols-2">
          {blocks.map((b, i) => (
            <Reveal key={b.title} delay={i % 2}>
              <div className="h-full rounded-2xl border border-brand-border bg-white p-7">
                <h2 className="font-heading text-[20px] font-700 text-brand-ink">{b.title}</h2>
                <ul className="mt-4 space-y-2.5">
                  {b.items.map((it) => (
                    <li key={it} className="flex items-start gap-2.5 text-[15px] text-brand-muted"><CheckCircle2 className="mt-0.5 h-4.5 w-4.5 shrink-0 text-brand-primary" style={{ width: 18, height: 18 }} />{it}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-brand-alt py-16">
        <div className="container-page">
          <SectionHeader eyebrow="Workflow" title="How we deliver this service" />
          <div className="mt-10 grid gap-6 lg:grid-cols-5">
            {s.workflow.map((w, i) => (
              <Reveal key={w} delay={i}>
                <div className="rounded-2xl border border-brand-border bg-white p-6">
                  <span className="font-heading text-[15px] font-800 text-brand-gold">0{i + 1}</span>
                  <p className="mt-3 text-[15px] font-600 text-brand-ink">{w}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container-page grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-brand-border bg-white p-7">
            <div className="mb-3 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-brand-primary" /><h2 className="font-heading text-[20px] font-700 text-brand-ink">Quality considerations</h2></div>
            <p className="text-[15px] leading-relaxed text-brand-muted">{s.quality}</p>
          </div>
          <div className="rounded-2xl border border-brand-warning/30 bg-[#FBF6EC] p-7">
            <div className="mb-3 flex items-center gap-2"><Lock className="h-5 w-5 text-brand-warning" /><h2 className="font-heading text-[20px] font-700 text-brand-ink">Confidentiality note</h2></div>
            <p className="text-[15px] leading-relaxed text-brand-ink/80">Do not submit confidential records through the general enquiry form. Secure transfer arrangements are discussed after an initial review. We do not claim specific compliance certifications.</p>
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="container-page">
          <SectionHeader eyebrow="FAQ" title="Frequently asked questions" />
          <div className="mt-8 max-w-3xl">
            <Accordion type="single" collapsible>
              {FAQS.slice(0, 5).map((f, i) => (
                <AccordionItem key={i} value={`f${i}`} className="border-brand-border">
                  <AccordionTrigger className="text-left font-heading text-[16px] font-600 text-brand-ink hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-relaxed text-brand-muted">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="bg-brand-alt py-16">
        <div className="container-page">
          <SectionHeader eyebrow="Related" title="Related services" />
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {related.map(([k, r]) => (
              <Link key={k} to={`/services/${k}`} className="group rounded-2xl border border-brand-border bg-white p-6 transition-transform hover:-translate-y-1" data-testid={`related-${k}`}>
                <h3 className="font-heading text-[18px] font-700 text-brand-ink">{r.title}</h3>
                <p className="mt-2 text-[14px] text-brand-muted line-clamp-2">{r.lead}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-[14px] font-600 text-brand-primary">View <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
